/**
 * @name: loadComponents
 * @description: 组件自动加载工具 - 统一的组件加载逻辑（支持懒加载）
 * @author: AI Assistant
 * @date: 2024-12-23
 * @update: 2026-01-08 - 添加异步懒加载支持
 */

import { DefineComponent, defineAsyncComponent } from 'vue';

/**
 * 加载指定目录下的所有组件（同步加载，旧版本）
 * @param modules - import.meta.globEager 返回的模块对象
 * @returns 组件映射对象
 */
export function loadComponents(modules: Record<string, any>): Record<string, DefineComponent> {
  const components: Record<string, DefineComponent> = {};

  for (const path in modules) {
    const comp = modules[path].default;
    
    // 优先使用组件的name属性，否则使用路径中的文件夹名
    const componentName = comp.name || path.split('/')[1];
    
    components[componentName] = comp;
  }

  return components;
}

/**
 * 加载指定目录下的所有组件（异步懒加载，推荐）
 * 使用 defineAsyncComponent 实现按需加载，提升首屏性能
 * @param modules - import.meta.glob 返回的模块对象
 * @returns 组件映射对象
 */
export function loadAsyncComponents(modules: Record<string, any>): Record<string, DefineComponent> {
  const components: Record<string, DefineComponent> = {};

  for (const path in modules) {
    const componentName = path.split('/')[1];
    
    // 使用 defineAsyncComponent 实现懒加载
    components[componentName + 'Ctl'] = defineAsyncComponent({
      loader: modules[path] as any,
      // 延迟显示 loading 组件的时间，默认 200ms
      delay: 200,
      // 超时时间 30秒
      timeout: 30000
    });
  }

  return components;
}
